/**
 * Create/update a semester feedback schedule master (admin)
 */

export default function InitiateFeedbackCyclePanel({
  cycleForm,
  setCycleForm,
  availableSemesters,
  onSubmit,
  submitting,
  error,
  className = '',
}) {
  const cycleName = `Student Feedback Schedule ${cycleForm.academicYear} | Semester ${cycleForm.semester}`;
  const toDateTimeInput = (value, fallbackTime) => {
    if (!value) return '';
    return String(value).length === 10 ? `${value}T${fallbackTime}` : String(value).slice(0, 16);
  };

  return (
    <div className={`cycle-panel admin-card ${className}`.trim()}>
      <div className="admin-card-body">
        <div className="cycle-panel-header">
          <div className="cycle-panel-header-top">
            <h2 className="admin-section-title m-0">Set student feedback timeline</h2>
            <div className="cycle-preview-badge">
              <span className="cycle-preview-label">Selected schedule</span>
              <span className="cycle-preview-value">{cycleName}</span>
            </div>
          </div>
          <p className="cycle-panel-description">
            Select the academic year, semester, and feedback window. This single timeline applies to all programs, departments, divisions, subjects, and faculty for that semester.
          </p>
        </div>

        {error && (
          <div className="alert-error mt-4" role="alert">
            {error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="cycle-form mt-6"
        >
          <div className="table-wrap">
            <table className="table cycle-input-table">
              <thead>
                <tr>
                  <th>Academic year</th>
                  <th>Semester</th>
                  <th>Start date and time</th>
                  <th>End date and time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      id="cycle-academic-year"
                      value={cycleForm.academicYear}
                      className="input cycle-table-input"
                      aria-label="Academic year"
                      readOnly
                    />
                  </td>
                  <td>
                    <select
                      id="cycle-semester"
                      value={cycleForm.semester}
                      onChange={(e) => setCycleForm((f) => ({ ...f, semester: Number(e.target.value) }))}
                      className="input cycle-table-input"
                      aria-label="Semester"
                    >
                      {(availableSemesters || [1, 2, 3, 4, 5, 6, 7, 8]).map((s) => (
                        <option key={s} value={s}>Semester {s}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      id="cycle-start-date"
                      type="datetime-local"
                      value={toDateTimeInput(cycleForm.startDate, '09:00')}
                      onChange={(e) => setCycleForm((f) => ({ ...f, startDate: e.target.value }))}
                      className="input cycle-table-input"
                      required
                      aria-label="Start date and time"
                    />
                  </td>
                  <td>
                    <input
                      id="cycle-end-date"
                      type="datetime-local"
                      value={toDateTimeInput(cycleForm.endDate, '17:00')}
                      onChange={(e) => setCycleForm((f) => ({ ...f, endDate: e.target.value }))}
                      className="input cycle-table-input"
                      required
                      aria-label="End date and time"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="cycle-options-row">
            <label className="cycle-checkbox-label" htmlFor="cycle-anonymous">
              <input
                id="cycle-anonymous"
                type="checkbox"
                checked={Boolean(cycleForm.isAnonymous)}
                onChange={(e) => setCycleForm((f) => ({ ...f, isAnonymous: e.target.checked }))}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                <span className="cycle-checkbox-title">Allow anonymous feedback</span>
                <span className="cycle-checkbox-hint">Student identities are hidden in reports when enabled.</span>
              </span>
            </label>
          </div>

          <div className="cycle-form-footer">
            <button type="submit" disabled={submitting} className="btn-primary px-6 py-2.5">
              {submitting ? 'Saving...' : 'Save schedule'}
            </button>
            <p className="cycle-form-hint">
              This only saves the timeline. Use Activate and notify from the schedule list when the form should be opened for students.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
